import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CakeIcon from '@mui/icons-material/Cake';
import GroupIcon from '@mui/icons-material/Group';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useParams } from 'react-router-dom';
import { EDUCACION, GRUPOS, GRUPOS_KEYS, SEXOS, VIVEN } from '../model/pacienteConstants';
import { usePacienteForm } from '../hooks/usePacienteForm';
import { formatDateOnly } from '../../../shared/utils/dateOnly';

const COLORS = {
  primary: '#0d6aee',
  primaryDark: '#1d4ed8',
  textMain: '#0f172a',
  textMuted: '#475569',
  labelMuted: '#64748b',
  border: '#dbe7f3',
  surfaceSoft: '#e2e8f0',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 },
  },
};

const selectSx = {
  borderRadius: '10px',
  backgroundColor: '#ffffff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary, borderWidth: 2 },
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 4,
  border: `1px solid ${COLORS.border}`,
  background: '#ffffff',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
};

export default function PacienteForm() {
  const { idPaciente } = useParams();
  const navigate = useNavigate();
  const {
    formData,
    errors,
    openGood,
    setOpenGood,
    openError,
    setOpenError,
    openDuplicateDialog,
    setOpenDuplicateDialog,
    duplicatePacientes,
    searchingDuplicates,
    handleChange,
    handleSave,
    savePaciente,
  } = usePacienteForm({ idPaciente, navigate });

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ maxWidth: 1080, width: '100%' }}>

        {/* Hero */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 4,
            border: `1px solid ${COLORS.border}`,
            background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Box>
              <Typography variant="overline" sx={{ color: COLORS.primaryDark, letterSpacing: 1.4 }}>
                Sistema de seguimiento clínico
              </Typography>
              <Typography variant="h4" sx={{ color: COLORS.textMain, fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
                {idPaciente ? 'Editar paciente' : 'Nuevo paciente'}
              </Typography>
              <Typography sx={{ color: COLORS.textMuted, maxWidth: 600 }}>
                {idPaciente
                  ? 'Modifica los datos del paciente registrado en el sistema.'
                  : 'Completa todos los campos para registrar un nuevo paciente.'}
              </Typography>
            </Box>
            <Chip
              label={idPaciente ? 'Edición' : 'Registro nuevo'}
              variant="outlined"
              sx={{ borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }}
            />
          </Stack>
        </Paper>

        {/* Duplicate warnings */}
        {!idPaciente && searchingDuplicates && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3, borderRadius: 3 }}>
            Buscando pacientes con nombres similares…
          </Alert>
        )}
        {!idPaciente && duplicatePacientes.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {duplicatePacientes.length} paciente(s) con nombre similar encontrado(s):
            </Typography>
            {duplicatePacientes.slice(0, 3).map((p, idx) => (
              <Typography key={idx} variant="body2">
                {p.firstname} {p.lastname}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Datos personales */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: COLORS.primary }} />
            <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700 }}>
              Datos personales
            </Typography>
          </Box>
          <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nombre"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                error={!!errors.firstname}
                helperText={errors.firstname}
                InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} /> }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Apellidos"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                error={!!errors.lastname}
                helperText={errors.lastname}
                InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} /> }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Fecha de nacimiento"
                type="date"
                name="dateBirth"
                value={formData.dateBirth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                error={!!errors.dateBirth}
                helperText={errors.dateBirth}
                InputProps={{ startAdornment: <CakeIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} /> }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Teléfono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                error={!!errors.telefono}
                helperText={errors.telefono}
                InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} /> }}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Información adicional */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <GroupIcon sx={{ color: COLORS.primary }} />
            <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700 }}>
              Información adicional
            </Typography>
          </Box>
          <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.grupo}>
                <InputLabel>Grupo</InputLabel>
                <Select name="grupo" value={formData.grupo} onChange={handleChange} label="Grupo" sx={selectSx}>
                  {GRUPOS_KEYS.length === 0 && <MenuItem disabled value="">Sin grupos configurados</MenuItem>}
                  {GRUPOS_KEYS.map((key) => <MenuItem key={key} value={key}>{GRUPOS[key]}</MenuItem>)}
                </Select>
                {errors.grupo && <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>{errors.grupo}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.sexo}>
                <InputLabel>Sexo</InputLabel>
                <Select name="sexo" value={formData.sexo} onChange={handleChange} label="Sexo" sx={selectSx}>
                  {Object.keys(SEXOS).map((key) => <MenuItem key={key} value={key}>{key}</MenuItem>)}
                </Select>
                {errors.sexo && <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>{errors.sexo}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.viven}>
                <InputLabel>Convivencia</InputLabel>
                <Select name="viven" value={formData.viven} onChange={handleChange} label="Convivencia" sx={selectSx}>
                  {Object.keys(VIVEN).map((key) => <MenuItem key={key} value={key}>{key}</MenuItem>)}
                </Select>
                {errors.viven && <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>{errors.viven}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.nivelEducacional}>
                <InputLabel>Nivel educacional</InputLabel>
                <Select name="nivelEducacional" value={formData.nivelEducacional} onChange={handleChange} label="Nivel educacional" sx={selectSx}>
                  {Object.keys(EDUCACION).map((key) => <MenuItem key={key} value={key}>{key}</MenuItem>)}
                </Select>
                {errors.nivelEducacional && <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>{errors.nivelEducacional}</Typography>}
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Grupos de programa */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FitnessCenterIcon sx={{ color: COLORS.primary }} />
            <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700 }}>
              Grupos de programa
            </Typography>
          </Box>
          <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
          <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 2 }}>
            Selecciona el grupo o grupos al que pertenece el paciente (puede pertenecer a más de uno).
          </Typography>
          <Grid container spacing={2}>
            {[
              { name: 'grupoREVI',       label: 'REVI',       desc: 'Revitalización', color: '#dcfce7', textColor: '#166534' },
              { name: 'grupoEquilibrio', label: 'Equilibrio', desc: 'Equilibrio',      color: '#dbeafe', textColor: '#1e3a8a' },
              { name: 'grupoSP',         label: 'SP',         desc: 'Suelo Pélvico',  color: '#fce7f3', textColor: '#9d174d' },
              { name: 'grupoVent',       label: 'Vent',       desc: 'Ventilación',    color: '#fef3c7', textColor: '#92400e' },
            ].map(({ name, label, desc, color, textColor }) => {
              const checked = !!formData[name];
              return (
                <Grid item xs={6} sm={3} key={name}>
                  <Box
                    onClick={() =>
                      handleChange({ target: { name, type: 'checkbox', checked: !checked } })
                    }
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      border: `2px solid ${checked ? textColor : '#e2e8f0'}`,
                      backgroundColor: checked ? color : '#f8fafc',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      transition: 'all 0.18s ease',
                      userSelect: 'none',
                      '&:hover': { borderColor: textColor, backgroundColor: color },
                    }}
                  >
                    <Checkbox
                      name={name}
                      checked={checked}
                      onChange={handleChange}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        p: 0,
                        color: checked ? textColor : '#94a3b8',
                        '&.Mui-checked': { color: textColor },
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: checked ? textColor : COLORS.textMain }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: checked ? textColor : COLORS.textMuted, textAlign: 'center' }}>
                      {desc}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        {/* Actions */}
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 4,
            border: `1px solid ${COLORS.border}`,
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
            <Button variant="outlined" onClick={() => navigate('/home')} sx={{ borderRadius: 2.5 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ borderRadius: 2.5, px: 3 }}
            >
              {idPaciente ? 'Actualizar paciente' : 'Guardar paciente'}
            </Button>
          </Stack>
        </Paper>

        {/* Duplicate dialog */}
        <Dialog open={openDuplicateDialog} onClose={() => setOpenDuplicateDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#f59e0b' }} />
              <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700 }}>
                Posibles pacientes duplicados
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Se encontraron pacientes con nombre similar. ¿Seguro que desea crear este paciente?
            </Alert>
            <Typography variant="body2" sx={{ color: COLORS.labelMuted, fontWeight: 600, mb: 1 }}>
              Pacientes encontrados:
            </Typography>
            <List disablePadding>
              {duplicatePacientes.map((paciente, index) => (
                <ListItem
                  key={index}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: '#f8fbff',
                  }}
                >
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 600, color: COLORS.textMain }}>{paciente.firstname} {paciente.lastname}</Typography>}
                    secondary={
                      <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                        Teléfono: {paciente.telefono}
                        {paciente.dateBirth && ` • Nacimiento: ${formatDateOnly(paciente.dateBirth)}`}
                      </Typography>
                    }
                  />
                  <Chip label={`ID: ${paciente.id}`} size="small" variant="outlined" sx={{ borderColor: COLORS.border, color: COLORS.primary, fontWeight: 600 }} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setOpenDuplicateDialog(false)} variant="outlined" sx={{ borderRadius: 2.5 }}>
              Cancelar
            </Button>
            <Button
              onClick={() => { setOpenDuplicateDialog(false); savePaciente(); }}
              variant="contained"
              color="warning"
              sx={{ borderRadius: 2.5 }}
            >
              Crear de todas formas
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>
            {idPaciente ? 'Paciente editado correctamente.' : 'Paciente creado correctamente.'}
          </Alert>
        </Snackbar>
        <Snackbar open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
          <Alert severity="error" onClose={() => setOpenError(false)}>
            {idPaciente ? 'Error al editar el paciente.' : 'Error al crear el paciente.'}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
